// PanelSystem.ts
import * as THREE from 'three';
import mapboxgl from 'mapbox-gl';
import type { PanelData, PanelRow } from '../core/types';
import { SEVERITY_COLORS } from '../core/constants';
import type { SceneSystem } from './SceneSystem';

export class PanelSystem {
  private map: mapboxgl.Map;
  private scene: SceneSystem;
  private panels = new Map<string, mapboxgl.Popup>();
  private trackingIntervals: number[] = [];

  constructor(map: mapboxgl.Map, scene: SceneSystem) {
    this.map = map;
    this.scene = scene;
  }

  private worldToLngLat(worldCoord: [number, number]): [number, number] {
    return this.scene.worldToLngLat(new THREE.Vector3(worldCoord[0], 0, worldCoord[1]));
  }

  show(data: PanelData): void {
    // Remove only the existing panel with same ID if any
    if (this.panels.has(data.id)) {
      this.remove(data.id);
    }

    const html = this.buildHtml(data);
    const lngLat = this.worldToLngLat(data.coordinates);
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'scenario-panel-popup',
      maxWidth: '220px',
      offset: [0, -90],
    })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(this.map);

    this.panels.set(data.id, popup);
  }

  update(id: string, rows: PanelRow[]): void {
    const el = document.getElementById(`panel-body-${id}`);
    if (!el) return;
    el.innerHTML = rows.map(r => this.buildRow(r)).join('');
  }

  remove(id: string): void {
    const popup = this.panels.get(id);
    if (popup) {
      popup.remove();
      this.panels.delete(id);
    }
  }

  trackPosition(panelId: string, getPosition: () => [number, number] | null): void {
    let animId = 0;
    const update = () => {
      const pos = getPosition();
      const popup = this.panels.get(panelId);
      if (pos && popup) {
        popup.setLngLat(this.worldToLngLat(pos));
        animId = requestAnimationFrame(update);
      }
    };
    animId = requestAnimationFrame(update);
    this.trackingIntervals.push(animId);
  }

  removeAll(): void {
    this.panels.forEach(p => p.remove());
    this.panels.clear();
    this.trackingIntervals.forEach(id => cancelAnimationFrame(id));
    this.trackingIntervals = [];
  }

  dispose(): void {
    this.removeAll();
  }

  private buildHtml(data: PanelData): string {
    const typeIcon = (t: string) => {
      switch (t) {
        case 'vehicle': return '&#x1F69B;';
        case 'infrastructure': return '&#x26A1;';
        case 'sensor': return '&#x1F4E1;';
        default: return '&#x1F4CA;';
      }
    };

    return `
      <div class="scenario-panel">
        <div class="scenario-panel-header">
          <span class="scenario-panel-icon">${typeIcon(data.panelType)}</span>
          <span class="scenario-panel-title">${data.title}</span>
        </div>
        <div class="scenario-panel-body" id="panel-body-${data.id}">
          ${data.data.map(r => this.buildRow(r)).join('')}
        </div>
      </div>
    `;
  }

  private buildRow(row: PanelRow): string {
    const color = row.status ? SEVERITY_COLORS[row.status] || 'var(--text-primary)' : 'var(--text-primary)';
    return `
      <div class="scenario-panel-row">
        <span class="scenario-panel-label">${row.label}</span>
        <span class="scenario-panel-value" style="color: ${color}">${row.value}</span>
      </div>
    `;
  }
}
